/**
 * Where a staff notification should take the operator.
 *
 * The bell handled exactly one reference (`Booking` → `/bookings`) and dumped
 * everything else on `/notifications`, which is the page they were already
 * looking at. The notifications table itself had no click handler at all.
 *
 * Every destination below is a route registered in `Routes/allRoutes.ts`, and
 * comes in one of three shapes:
 *
 * - **detail** — the route renders that one record. Only two do:
 *   `/pages-account/:id` for a person and `/job-details?jobId=` for a job.
 * - **focus** — a list page, where `referenceId` names a row it renders. The id
 *   goes as `?focus=`, which `useFocusedRow` reads to scroll to and ring it.
 * - **list** — a list page whose rows are *not* of the referenced type (a job
 *   application id on the jobs list, an invoice id on the bookings list). The
 *   id is deliberately dropped: attaching it would only produce a spurious
 *   "not on this page" notice.
 *
 * Kept in step with mynda-ops-alert's `src/lib/deepLink.ts`, which resolves the
 * same categories for links leaving that app.
 */
export interface RoutableNotification {
  reference?: string;
  referenceId?: string;
  category?: string;
}

type Destination =
  | { kind: "detail"; build: (id: string) => string; fallback: string }
  | { kind: "focus"; path: string }
  | { kind: "list"; path: string };

const account: Destination = {
  kind: "detail",
  build: (id) => `/pages-account/${id}`,
  fallback: "/apps-users-list",
};

const jobDetail: Destination = {
  kind: "detail",
  build: (id) => `/job-details?jobId=${id}`,
  fallback: "/jobs",
};

/** `reference` values, mirroring the API's `NotificationTypes`. */
const BY_REFERENCE: Record<string, Destination> = {
  Booking: { kind: "focus", path: "/bookings" },
  // referenceId is the invoice's id, but this page is keyed by booking, and
  // there is no invoice list — so the operator lands where invoices are
  // reachable and opens it from the booking.
  Invoice: { kind: "list", path: "/bookings" },
  Transaction: { kind: "focus", path: "/transactions" },
  TransactionFailed: { kind: "focus", path: "/transactions" },
  Withdrawal: { kind: "focus", path: "/transactions" },
  // The webhook sites that raise this pass `referenceId: ""`.
  walletTopup: { kind: "list", path: "/wallets" },
  JobPost: jobDetail,
  // referenceId is the *application*, which the jobs list does not render.
  JobApplication: { kind: "list", path: "/jobs" },
  // mynda.controller passes the job id here, not the offer's.
  JobOffer: jobDetail,
  Meeting: { kind: "focus", path: "/events" },
  InterviewScheduled: { kind: "focus", path: "/events" },
  InterviewResponse: { kind: "focus", path: "/events" },
  CsrTicket: { kind: "focus", path: "/csr" },
  Complaint: { kind: "focus", path: "/complaints" },
  LabTestOrder: { kind: "focus", path: "/lab-test-orders" },
  NewOrder: { kind: "focus", path: "/lab-test-orders" },
  BookLabAppointmentDate: { kind: "focus", path: "/lab-test-orders" },
  VerificationOrder: { kind: "focus", path: "/verification-orders" },
  // Named for the service type, but every site passes the *order* id — and
  // `/verification-services` is the catalogue of types, which would never
  // contain it.
  VerificationService: { kind: "focus", path: "/verification-orders" },
  UserRegistration: account,
  AccountDeletion: account,
  InspectionScheduled: { kind: "focus", path: "/inspections" },
  Kyc: account,
};

/**
 * Fallback for the many admin alerts whose `reference` is unreliable — the
 * wallet controllers historically passed payment-gateway strings rather than a
 * notification type. `category` is set by the engagement engine and the newer
 * alert sites, and is the more trustworthy of the two when present.
 */
const BY_CATEGORY: Record<string, Destination> = {
  csr: { kind: "focus", path: "/csr" },
  verification: { kind: "focus", path: "/verification-orders" },
  withdrawal: { kind: "focus", path: "/transactions" },
  payment: { kind: "focus", path: "/transactions" },
  lab_order: { kind: "focus", path: "/lab-test-orders" },
  complaint: { kind: "focus", path: "/complaints" },
  placement: { kind: "focus", path: "/placements" },
  booking: { kind: "focus", path: "/bookings" },
  registration: account,
  account_deletion: account,
  job_post: jobDetail,
  interview: { kind: "focus", path: "/events" },
  inspection: { kind: "focus", path: "/inspections" },
};

export const notificationRoute = (
  notification: RoutableNotification
): string | null => {
  const destination =
    (notification.reference && BY_REFERENCE[notification.reference]) ??
    (notification.category && BY_CATEGORY[notification.category]) ??
    null;

  if (!destination) return null;

  const id = notification.referenceId;

  if (destination.kind === "detail") {
    // Without an id the detail route renders blank; the list is searchable.
    return id ? destination.build(encodeURIComponent(id)) : destination.fallback;
  }

  if (destination.kind === "list" || !id) return destination.path;

  return `${destination.path}?focus=${encodeURIComponent(id)}`;
};

export default notificationRoute;
